/**
 * Analytics helpers shared by skupreme-client and skupreme-warehouse-dashboard.
 * Kept free of any analytics SDK so both apps can use them and they test without one.
 */

const OBJECT_ID = /^[a-f0-9]{24}$/i;
const UUID = /^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/i;
const NUMERIC = /^\d+$/;
// Order numbers, tracking numbers and tokens: long, and mixing letters with digits.
const OPAQUE_TOKEN = /^(?=.*\d)(?=.*[a-z])[a-z0-9_-]{12,}$/i;

/**
 * Collapses record ids in a path so that every order, SKU or shipment page
 * counts as the same screen: `/orders/64f0c2…/edit` → `/orders/:id/edit`.
 */
export function routePattern(pathname: string): string {
  const segments = pathname.split('/').map(segment => {
    if (!segment) return segment;
    const decoded = safeDecode(segment);
    if (
      OBJECT_ID.test(decoded) ||
      UUID.test(decoded) ||
      NUMERIC.test(decoded) ||
      OPAQUE_TOKEN.test(decoded)
    ) {
      return ':id';
    }
    return segment;
  });

  const joined = segments.join('/');
  return joined.length > 1 ? joined.replace(/\/+$/, '') : joined || '/';
}

/** Query parameter skupreme.com adds to signup links so one visitor can be followed into the app. */
export const ANALYTICS_ID_PARAM = 'ph_id';

const DISTINCT_ID = /^[A-Za-z0-9_.:-]{8,200}$/;

/**
 * Reads the anonymous analytics id passed from the marketing site, or `undefined`
 * when it is absent or does not look like one.
 */
export function analyticsIdFromSearch(search: string): string | undefined {
  const value = new URLSearchParams(search).get(ANALYTICS_ID_PARAM);
  return value && DISTINCT_ID.test(value) ? value : undefined;
}

function safeDecode(segment: string): string {
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
}

/**
 * Drops the query string and fragment from a URL, keeping origin and path.
 * Anything that is not a URL comes back unchanged. Used before an event
 * reaches PostHog: sign-in codes travel in the fragment, share tokens in the
 * query, and session recording is on, so neither may be recorded.
 */
export function stripUrlSecrets(value: unknown): unknown {
  if (typeof value !== 'string') return value;
  const cut = value.search(/[?#]/);
  return cut === -1 ? value : value.slice(0, cut);
}

const URL_PROPERTY = /(url|referrer)$/i;

/**
 * Applies `stripUrlSecrets` to every URL-shaped property PostHog attaches to
 * an event (`$current_url`, `$referrer`, `$initial_current_url`, …), at the
 * top level and inside `$set` / `$set_once`. Mutates and returns the object.
 */
export function scrubAnalyticsUrls<T extends Record<string, unknown>>(
  properties: T | undefined | null,
): T | undefined | null {
  if (!properties || typeof properties !== 'object') return properties;
  for (const key of Object.keys(properties)) {
    const value = properties[key];
    if (URL_PROPERTY.test(key)) {
      (properties as Record<string, unknown>)[key] = stripUrlSecrets(value);
    } else if (
      (key === '$set' || key === '$set_once') &&
      value &&
      typeof value === 'object'
    ) {
      scrubAnalyticsUrls(value as Record<string, unknown>);
    }
  }
  return properties;
}
